import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createIndependentPaymentStages,
  deleteIndependentPaymentStages,
  getIndependentPaymentStages,
  handleCheckIndepdendentPaymentStageDelete,
  updateIndependentPaymentStages,
  updateUnitPaymentStage,
  getUnitsByProjectId
} from '../../services/api/implementation/impl';
import Modal from '../../../shared/components/ui/Modal';
import AddPaymentStage from '../forms/AddPaymentStage';
import EditPaymentStages from '../../pages/EditPaymentStages';
import AddBulkPaymentStage from '../forms/AddBulkPaymentStage';
import toast from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import Table from '../../../shared/components/layout/Table';
import { transformData } from '../../../super-admin/utils/transformData';
import { ChevronDown, Plus } from 'lucide-react';
import Button from '../../../shared/components/ui/Button';
import CommonPaymentModals from './CommonPaymentModals';
import { invalidateAccountsAfterStageChange } from '../../utils/invalidateAccountsAfterStageChange';

const IndependentPaymentStages = ({ projectId, hideAddButton = false, showActions = true }) => {
  const queryClient = useQueryClient();
  const userId = Cookies.get('userId');
  const [selectedUnitId, setSelectedUnitId] = useState(null);
  const [independentUnits, setIndependentUnits] = useState([]);
  const [isOpenPaymentStageModal, setIsOpenPaymentStagesModal] = useState(false);
  const [isOpenEditPaymentStageModal, setIsOpenEditPaymentStagesModal] = useState(false);
  const [paymentStagesId, setpaymentStagesId] = useState();
  const [Id, setId] = useState();
  const [isCompletedPayment, setIsCompletedPayment] = useState(false);
  const [paymentConfirmId, setpaymentConfirmId] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [isCurrentDay, setIsCurrentDay] = useState(true);
  const [isEditAllPayments, setIsEditAllPayments] = useState(false);
  const [incompletePayments, setIncompletePayments] = useState([]);
  const [isOpenBulkPaymentStageModal, setIsOpenBulkPaymentStageModal] = useState(false);
  const [canDelete, setCanDelete] = useState(null);
  const [resData, setResData] = useState();
  const [selectedIds, setSelectedIds] = useState([]);
  const [showChoiceModal, setShowChoiceModal] = useState(false);
  const [showUnitwiseModal, setShowUnitwiseModal] = useState(false);
  const [selectedPaymentStage, setSelectedPaymentStage] = useState(null);
  const [units, setUnits] = useState([]);
  const [isCompletingUnit, setIsCompletingUnit] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [isUnitDropdownOpen, setIsUnitDropdownOpen] = useState(false);

  // Fetch all units for the project
  const {
    data: allUnits,
    refetch: refetchUnits,
    error: unitsError
  } = useQuery({
    queryKey: ['all_units', projectId],
    queryFn: () => getUnitsByProjectId(projectId),
    enabled: !!projectId,
    onError: (error) => {
      console.error('Error fetching units:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to fetch units');
    }
  });

  // Fetch payment stages for selected unit
  const {
    data: AllPaymentStages,
    refetch: refetchpaymentstages,
    error: paymentStagesError
  } = useQuery({
    queryKey: ['payment_stages', selectedUnitId],
    queryFn: () => getIndependentPaymentStages(selectedUnitId),
    enabled: !!selectedUnitId,
    onError: (error) => {
      console.error('Error fetching payment stages:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to fetch payment stages'
      );
    }
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Independent Payment Stages Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  // Filter independent units from all units
  useEffect(() => {
    if (allUnits) {
      const unitsWithNoPaymentStages =
        allUnits?.filter((unit) => unit.block?.type === 'Independent') || [];
      setIndependentUnits(unitsWithNoPaymentStages);

      // Set initial selection
      if (unitsWithNoPaymentStages.length > 0 && !selectedUnitId) {
        const sortedUnits = unitsWithNoPaymentStages.sort((a, b) =>
          (a.unitName || `Unit ${a.id}`).localeCompare(b.unitName || `Unit ${b.id}`)
        );

        setSelectedUnitId(sortedUnits[0].id);
        console.log('Initial independent unit selected:', sortedUnits[0].id);
      }
    }
  }, [allUnits, selectedUnitId]);

  const PaymentFormat = [
    'id',
    'unit',
    'name',
    'percentage',
    'status',
    'completion_date',
    'actions'
  ];

  const handlePaymentStagesFormat = (item, index) => {
    // Find the independent unit for this payment stage
    const independentUnit = independentUnits?.find((unit) => unit.id === item.unit_id);

    return {
      id: item.id,
      unit: index === 0 ? independentUnit?.unitName || `Unit ${item.unit_id}` : null,
      name: item.name,
      percentage: item.percentage,
      status: item.is_completed == true ? 'Completed' : (showActions ? 'PaymentActions' : 'Pending'),
      completion_date: item.completion_date
        ? new Date(item.completion_date).toLocaleDateString()
        : '-'
    };
  };

  const paymentdata =
    AllPaymentStages?.map((item, index) =>
      transformData(handlePaymentStagesFormat(item, index), PaymentFormat)
    ) || [];

  const handleSelectUnitChange = (id) => {
    console.log('New Selected Independent Unit ID:', id);
    const unit = independentUnits?.find((unit) => unit.id === id);
    if (unit) {
      setSelectedUnitId(unit.id);
    } else {
      setSelectedUnitId(null);
    }
  };

  const handleCreateNewPaymentStages = () => {
    setIsOpenPaymentStagesModal(!isOpenPaymentStageModal);
  };

  const { mutate: handleOnPaymentStage } = useMutation({
    mutationFn: (payload) => createIndependentPaymentStages(selectedUnitId, payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: () => {
      setIsSubmitting(false);
      toast.success('Payment Stages created Successfully');
      setIsOpenPaymentStagesModal(false);
      refetchpaymentstages();
    },
    onError: (error) => {
      setIsSubmitting(false);
      console.error('Error in payment stages:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Error in Creating Payment Stages'
      );
    }
  });

  const handleEditPayment = (id) => {
    setIsOpenEditPaymentStagesModal(true);
    setId(id);
    const singleStage = AllPaymentStages.find((stage) => stage.id === parseInt(id));
    if (singleStage) {
      setpaymentStagesId(singleStage);
    }
  };

  const { mutate: handleOnEditPaymentStage } = useMutation({
    mutationFn: (payload) => updateIndependentPaymentStages(payload, payload.id),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: () => {
      setIsSubmitting(false);
      toast.success('Payment Stage Updated Successfully');
      setIsOpenEditPaymentStagesModal(false);
      refetchpaymentstages();
    },
    onError: (error) => {
      setIsSubmitting(false);
      console.error('Error in updating payment stage:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Error in Updating Payment Stage'
      );
    }
  });

  const { mutate: handleMarkupdate } = useMutation({
    mutationFn: (payload) => {
      const currentDate = new Date();
      const istOffset = 5.5 * 60 * 60 * 1000;
      const istDate = new Date(currentDate.getTime() + istOffset);
      const date = isCurrentDay ? istDate.toISOString().split('T')[0] : selectedDate;

      if (isCompletingUnit) {
        return updateUnitPaymentStage(
          {
            is_completed: true,
            completion_date: date
          },
          parseInt(paymentConfirmId)
        );
      } else {
        return updateIndependentPaymentStages(
          {
            is_completed: true,
            completion_date: date
          },
          parseInt(paymentConfirmId)
        );
      }
    },
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: (data) => {
      setIsSubmitting(false);
      toast.success('Payment is Completed');
      refetchpaymentstages();
      invalidateAccountsAfterStageChange(queryClient);
      setIsCompletedPayment(false);
      setSelectedDate('');
      setIsCurrentDay(true);

      const wasCompletingUnit = isCompletingUnit;
      setIsCompletingUnit(false);

      if (wasCompletingUnit && showUnitwiseModal) {
        setUnits((prevUnits) =>
          prevUnits.map((unit) =>
            unit.id === parseInt(paymentConfirmId)
              ? {
                ...unit,
                is_completed: true,
                completion_date: data?.completion_date || new Date().toISOString().split('T')[0]
              }
              : unit
          )
        );
      }
    },
    onError: (error) => {
      setIsSubmitting(false);
      setIsCompletingUnit(false);
      console.error('Error in updating payment stage:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Error in completed payment');
    }
  });

  const handleUnitwiseMarkComplete = (mappingId) => {
    setpaymentConfirmId(mappingId);
    setIsCompletingUnit(true);
    setIsCompletedPayment(true);
  };

  const handleConfimation = async (id) => {
    setpaymentConfirmId(id);
    setIsCompletingUnit(false);

    const paymentStage = AllPaymentStages?.find((stage) => stage.id === parseInt(id));

    if (paymentStage?.unitwise) {
      setUnits(paymentStage?.unitMappings || []);
      setSelectedPaymentStage(paymentStage);
      setShowUnitwiseModal(true);
    } else {
      setIsCompletedPayment(!isCompletedPayment);
    }
  };

  const { mutate: handleBulkPaymentUpdate } = useMutation({
    mutationFn: async ({ updatedStages, deletedStageIds }) => {
      const deletePromises = deletedStageIds.map((id) => deleteIndependentPaymentStages(id));
      await Promise.all(deletePromises);

      const updatePromises = updatedStages.map((stage) =>
        updateIndependentPaymentStages(
          {
            name: stage.name,
            percentage: stage.percentage
          },
          stage.id
        )
      );

      await Promise.all(updatePromises);
    },
    onSuccess: () => {
      setIsSubmitting(false);
      toast.success('Payment stages updated successfully');
      setIsEditAllPayments(false);
      refetchpaymentstages();
    },
    onError: (error) => {
      setIsSubmitting(false);
      console.error('Error updating payment stages:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Error updating payment stages'
      );
    }
  });

  const handleEditAllPayments = () => {
    const incomplete = AllPaymentStages?.filter((stage) => !stage.is_completed) || [];
    setIncompletePayments(incomplete);
    setIsEditAllPayments(true);
  };

  const { mutate: handleOnBulkPaymentStage } = useMutation({
    mutationFn: (payload) => createIndependentPaymentStages(null, payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: () => {
      setIsSubmitting(false);
      toast.success('Bulk Payment Stages created Successfully');
      setIsOpenBulkPaymentStageModal(false);
      refetchpaymentstages();
    },
    onError: (error) => {
      setIsSubmitting(false);
      console.error('Error in bulk payment stages:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Error in Creating Bulk Payment Stages'
      );
    }
  });

  const handleCreateBulkPaymentStages = () => {
    setIsOpenBulkPaymentStageModal(!isOpenBulkPaymentStageModal);
  };

  const handleCloneSuccess = () => {
    // Refetch payment stages and units after successful clone
    refetchpaymentstages();
    refetchUnits();
  };

  // Delete functions
  const { mutate: handleCheckDelete } = useMutation({
    mutationFn: (id) => handleCheckIndepdendentPaymentStageDelete(id),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: async (res, id) => {
      setIsSubmitting(false);
      const payment = AllPaymentStages?.find((item) => parseInt(item.id) === parseInt(id));
      const paymentName = payment?.name || 'payment';
      if (res.canDelete) {
        setCanDelete('yes');
        setResData({ name: paymentName });
        setSelectedIds([parseInt(id)]);
      } else {
        setResData({ ...res, name: paymentName });
        setCanDelete('no');
      }
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error checking dependencies', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error checking dependencies');
    }
  });

  const { mutate: handleDeleteFn } = useMutation({
    mutationFn: (payload) => deleteIndependentPaymentStages(payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: () => {
      setIsSubmitting(false);
      toast.success('Payment Stage Deleted Successfully');
      refetchpaymentstages();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error deleting payment stage', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error deleting payment stage');
    }
  });

  const HandleDelete = (data) => {
    const payload = { ids: data };
    handleDeleteFn(payload);
  };

  const transformedIndependentUnits =
    independentUnits
      ?.map((unit) => ({
        id: unit.id,
        value: unit.id,
        label: unit.unitName + ' (' + unit.block.name + ')' || `Unit ${unit.id}`
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) || [];

  const selectedUnitLabel =
    transformedIndependentUnits.find((unit) => unit.id === selectedUnitId)?.label || 'Select Unit';

  if (unitsError) {
    return <div>Error loading units: {unitsError.message}</div>;
  }

  return (
    <>
      {/* Modals */}
      {isOpenPaymentStageModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={`Payment Stages of '${transformedIndependentUnits.find((item) => item.id === selectedUnitId)?.label || 'Independent Unit'}'`}
          width={55}
          onClose={() => setIsOpenPaymentStagesModal(false)}>
          <AddPaymentStage
            isSubmitting={isSubmitting}
            blockId={selectedUnitId}
            handleOnPaymentStage={handleOnPaymentStage}
            onClose={() => setIsOpenPaymentStagesModal(false)}
            isIndependent={true}
          />
        </Modal>
      )}

      {isOpenBulkPaymentStageModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={'Bulk Payment Stages'}
          width={55}
          onClose={() => setIsOpenBulkPaymentStageModal(false)}>
          <AddBulkPaymentStage
            isSubmitting={isSubmitting}
            projectId={projectId}
            handleOnPaymentStage={handleOnBulkPaymentStage}
            onClose={() => setIsOpenBulkPaymentStageModal(false)}
            isIndependent={true}
          />
        </Modal>
      )}

      {isOpenEditPaymentStageModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={'Edit Payment Stage'}
          width={55}
          onClose={() => setIsOpenEditPaymentStagesModal(false)}>
          <EditPaymentStages
            handleOnEditPaymentStage={handleOnEditPaymentStage}
            isSubmitting={isSubmitting}
            singleStageData={paymentStagesId}
            onClose={() => setIsOpenEditPaymentStagesModal(false)}
            isIndependent={true}
          />
        </Modal>
      )}

      {isEditAllPayments && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={'Edit All Payment Stages'}
          width={55}
          onClose={() => setIsEditAllPayments(false)}>
          <EditPaymentStages
            isSubmitting={isSubmitting}
            allPaymentStages={AllPaymentStages}
            handleOnEditPaymentStage={handleBulkPaymentUpdate}
            onClose={() => setIsEditAllPayments(false)}
          />
        </Modal>
      )}

      {/* Common Modals */}
      <CommonPaymentModals
        showChoiceModal={showChoiceModal}
        setShowChoiceModal={setShowChoiceModal}
        handleCreateNewPaymentStages={handleCreateNewPaymentStages}
        handleCreateBulkPaymentStages={handleCreateBulkPaymentStages}
        showUnitwiseModal={showUnitwiseModal}
        setShowUnitwiseModal={setShowUnitwiseModal}
        selectedPaymentStage={selectedPaymentStage}
        units={units}
        setUnits={setUnits}
        isSubmitting={isSubmitting}
        handleUnitwiseMarkComplete={handleUnitwiseMarkComplete}
        isCompletedPayment={isCompletedPayment}
        setIsCompletedPayment={setIsCompletedPayment}
        isCurrentDay={isCurrentDay}
        setIsCurrentDay={setIsCurrentDay}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        handleMarkupdate={handleMarkupdate}
        isCompletingUnit={isCompletingUnit}
        setIsCompletingUnit={setIsCompletingUnit}
        canDelete={canDelete}
        setCanDelete={setCanDelete}
        resData={resData}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        paymentdata={paymentdata}
        HandleDelete={HandleDelete}
        isIndependent={true}
        showCloneModal={showCloneModal}
        setShowCloneModal={setShowCloneModal}
        projectId={projectId}
        handleCloneSuccess={handleCloneSuccess}
      />

      {/* Main Content */}
      {paymentStagesError || !independentUnits || independentUnits.length === 0 ? (
        <div className="flex justify-center h-[20rem]">
          {paymentStagesError ? 'No Data Found' : 'No Independent Units Found'}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <label htmlFor="independent-unit-filter" className="text-sm font-medium text-gray-700">
              Unit Filter
            </label>
            <div className="relative min-w-[16rem]" id="independent-unit-filter">
              <button
                type="button"
                onClick={() => setIsUnitDropdownOpen((prev) => !prev)}
                className="flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 text-sm">
                <span className="truncate text-left">{selectedUnitLabel}</span>
                <ChevronDown
                  className={`h-4 w-4 text-gray-500 transition-transform ${
                    isUnitDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isUnitDropdownOpen && (
                <>
                  <div className="absolute top-full left-0 z-30 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                    {transformedIndependentUnits.map((unit) => (
                      <button
                        key={unit.id}
                        type="button"
                        onClick={() => {
                          handleSelectUnitChange(unit.id);
                          setIsUnitDropdownOpen(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                          unit.id === selectedUnitId ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                        }`}>
                        {unit.label}
                      </button>
                    ))}
                  </div>
                  <div
                    className="fixed inset-0 z-20"
                    onClick={() => setIsUnitDropdownOpen(false)}
                    role="button"
                    tabIndex={-1}
                  />
                </>
              )}
            </div>
          </div>
          <div className="table-container h-[calc(100vh-10rem)] overflow-auto">
          <style>{`
            .table-container .table-body {
              position: static !important;
              overflow-y: visible !important;
              max-height: none !important;
              height: auto !important;
              top: auto !important;
              padding-bottom: 0 !important;
            }
            .table-container .table-container {
              height: auto !important;
            }
            .table-container > div {
              height: auto !important;
            }
          `}</style>
          <Table
            headers={[
              'Unit',
              'Payment Stages',
              'Payment Percentage',
              'Status',
              'Date',
              ...(showActions ? ['Action'] : [])
            ]}
            data={paymentdata}
            isMarkEnable={showActions}
            identifier={'id'}
            onMark={(id) => handleConfimation(id)}
            onEdit={(id) => handleEditPayment(id)}
            isEditEnable={showActions}
            isDeleteEnable={showActions}
            onDelete={handleCheckDelete}
            isWrap={true}
          />
        </div>
        </div>
      )}

      {/* Floating Add Button */}
      {!hideAddButton && (
        <div className="fixed bottom-8 right-8 z-50">
          <Button
            displayType="primary"
            onClick={() => {
              mixpanel.track('Add Payment Stages Button Clicked', {
                user_id: userId,
                timestamp: new Date().toISOString()
              });
              setShowChoiceModal(true);
            }}
            customStyle={{
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}>
            <Plus size={24} color="white" />
          </Button>
        </div>
      )}
    </>
  );
};

export default IndependentPaymentStages;
